import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import AppFooter from '../components/AppFooter';
import {
  CreditCard, Package, ChevronRight, Check,
  Loader2, Sparkles, ArrowDownCircle,
  XCircle, RefreshCw, Zap, AlertTriangle, Trash2, Lock, MessageSquarePlus, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { usePricing, getTierForPaidItems, calculateMonthlyCost, calculateTotalCapacity } from '../config/pricing';
import ReferralShareWidget from '../components/ReferralShareWidget';
import FeedbackWidget from '../components/FeedbackWidget';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { BACKEND_URL } from '../config/api';

const BillingPage = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tierRates, freeBaseGrant, loading: pricingLoading } = usePricing();

  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showBillingFeedback, setShowBillingFeedback] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, paidItems: 0 });

  // Slider state
  const [sliderPaidItems, setSliderPaidItems] = useState(100);
  const [recommendedPaidItems, setRecommendedPaidItems] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Checkout cancelled. You can try again anytime.');
    }
  }, [searchParams]);

  useEffect(() => { fetchBillingData(); }, []);

  // Scroll to hash section (e.g. #pricing-calculator from Upgrade Now button)
  const location = useLocation();
  useEffect(() => {
    if (location.hash && !loading) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, [location.hash, loading]);

  useEffect(() => {
    if (billingStatus?.paid_item_slots > 0) {
      setSliderPaidItems(billingStatus.paid_item_slots);
    }
  }, [billingStatus]);

  // Auto-recommend slider position when coming from upgrade link
  useEffect(() => {
    if (!billingStatus || !location.hash) return;
    if (location.hash !== '#pricing-calculator') return;

    const currentItems = billingStatus.stash_item_count || 0;
    const freeItems = billingStatus.free_items_total || 20;
    const currentPaidSlots = billingStatus.paid_item_slots || 0;

    // Calculate recommended paid slots: current items - free + ~50 buffer, rounded up to nearest 50
    const neededPaid = Math.max(currentItems - freeItems + 50, currentPaidSlots + 50);
    const recommended = Math.min(Math.ceil(neededPaid / 50) * 50, 600);

    // Only auto-set if it's an upgrade (higher than current)
    if (recommended > currentPaidSlots) {
      setSliderPaidItems(Math.max(recommended, 100));
      setRecommendedPaidItems(Math.max(recommended, 100));
    }
  }, [billingStatus, location.hash]);

  const fetchBillingData = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/status`, {
        credentials: 'include', headers: getAuthHeaders()
      });
      if (res.ok) setBillingStatus(await res.clone().json());
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      toast.error('Payment verification timed out. Check your email for confirmation.');
      setCheckingPayment(false);
      return;
    }
    setCheckingPayment(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/checkout/status/${sessionId}`, {
        credentials: 'include', headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to check payment');
      const data = await res.clone().json();
      if (data.payment_status === 'paid') {
        toast.success(data.message || 'Subscription updated!');
        setCheckingPayment(false);
        navigate('/billing', { replace: true });
        await refreshUser();
        await fetchBillingData();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const showUpgradeConfirmation = (paidItems) => {
    setConfirmDialog({ open: true, paidItems });
  };

  const handleConfirmedCheckout = async () => {
    const paidItems = confirmDialog.paidItems;
    setConfirmDialog({ open: false, paidItems: 0 });
    setProcessingCheckout(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/checkout`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ paid_items: paidItems, origin_url: window.location.origin })
      });
      if (!res.ok) {
        const error = await res.clone().json();
        throw new Error(error.detail || 'Failed to start checkout');
      }
      const data = await res.clone().json();
      
      // Handle subscription update (existing subscriber) vs new checkout
      if (data.type === 'subscription_updated') {
        toast.success(data.message);
        await refreshUser();
        await fetchBillingData();
        setProcessingCheckout(false);
      } else if (data.checkout_url) {
        // Redirect to Stripe checkout for new subscribers
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Unexpected response from checkout');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to start checkout');
      setProcessingCheckout(false);
    }
  };

  // Helper to get confirmation dialog details
  const getConfirmationDetails = () => {
    const currentPaid = billingStatus?.paid_item_slots || 0;
    const currentTierObj = getTierForPaidItems(currentPaid, tierRates);
    const currentTier = currentTierObj?.name || currentTierObj?.tier || 'Free';
    const currentCost = currentPaid > 0 ? calculateMonthlyCost(currentPaid, tierRates) : 0;
    
    const newPaid = confirmDialog.paidItems;
    const newTierObj = getTierForPaidItems(newPaid, tierRates);
    const newTier = newTierObj?.name || newTierObj?.tier || 'Starter';
    const newCost = calculateMonthlyCost(newPaid, tierRates);
    
    const isUpgrade = newPaid > currentPaid;
    const isDowngrade = newPaid < currentPaid && currentPaid > 0;
    const isNewSubscription = currentPaid === 0;
    
    return { currentPaid, currentTier, currentCost, newPaid, newTier, newCost, isUpgrade, isDowngrade, isNewSubscription };
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/cancel`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders()
      });
      const data = await res.clone().json();
      if (res.ok) {
        toast.success(data.message);
        await refreshUser();
        await fetchBillingData();
      } else {
        toast.error(data.detail || 'Failed to cancel');
      }
    } catch { toast.error('Failed to cancel subscription'); }
    finally { 
      setCancelLoading(false);
      setShowCancelConfirm(false);
    }
  };

  // Slider calculation
  const sliderTier = getTierForPaidItems(sliderPaidItems, tierRates);
  const sliderCost = calculateMonthlyCost(sliderPaidItems, tierRates);
  const totalFreeItems = billingStatus?.free_items_total || freeBaseGrant;
  const referralFree = billingStatus?.free_items_breakdown?.referral_free || 0;
  const promoFree = billingStatus?.free_items_breakdown?.promo_free || 0;
  const sliderCapacity = calculateTotalCapacity(sliderPaidItems, totalFreeItems);
  const currentPaid = billingStatus?.paid_item_slots || 0;
  const isUpgrade = sliderPaidItems > currentPaid;
  const isDowngrade = sliderPaidItems < currentPaid && sliderPaidItems > 0;
  const isSame = sliderPaidItems === currentPaid;
  
  // Check if downgrade would exceed capacity (hard-lock)
  const currentItemCount = billingStatus?.stash_item_count || 0;
  const downgradeCapacity = sliderPaidItems + (billingStatus?.free_items_total || 20);
  const itemsOverLimit = currentItemCount - downgradeCapacity;
  const cannotDowngrade = isDowngrade && itemsOverLimit > 0;

  const getNudge = useCallback((paidItems) => {
    if (paidItems <= 0) return null;
    const currentTierInfo = getTierForPaidItems(paidItems, tierRates);
    for (let i = 0; i < tierRates.length - 1; i++) {
      if (tierRates[i].tier === currentTierInfo.tier) {
        const nextTier = tierRates[i + 1];
        const currentCost = Math.round(paidItems * currentTierInfo.price_per_item * 100) / 100;
        const nextCost = Math.round(nextTier.min_items * nextTier.price_per_item * 100) / 100;
        const crossoverAt = Math.floor(nextCost / currentTierInfo.price_per_item);
        if (paidItems >= crossoverAt) {
          return {
            targetPaidItems: nextTier.min_items,
            currentCost, targetCost: nextCost,
            savings: Math.round((currentCost - nextCost) * 100) / 100,
            extraItems: nextTier.min_items - paidItems,
          };
        }
      }
    }
    return null;
  }, [tierRates]);

  const nudge = getNudge(sliderPaidItems);

  if (loading || pricingLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <Navigation />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-sage" />
        </div>
      </div>
    );
  }

  const currentTier = billingStatus?.subscription_tier || 'free';
  const accountState = billingStatus?.account_state || 'active';
  const isUnlimited = accountState === 'unlimited';
  const isOverLimit = billingStatus?.is_over_limit || false;
  const itemsOver = billingStatus?.items_over_limit || 0;
  const usagePercent = isUnlimited ? 0
    : billingStatus
      ? Math.min((billingStatus.stash_item_count / Math.max(billingStatus.total_capacity, 1)) * 100, 150)
      : 0;

  const tierColors = {
    free: 'bg-gray-100 text-gray-700',
    starter: 'bg-blue-100 text-blue-700',
    crafter: 'bg-sage-light text-sage-dark',
    artisan: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-light text-charcoal mb-2">Billing & Subscription</h1>
          <p className="text-gray-soft">Manage your plan and stash capacity</p>
        </div>

        {checkingPayment && (
          <Card className="mb-6 border-sage bg-sage-light/20">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-sage" />
                <span className="text-sage-dark">Verifying your payment...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Over-Limit Hard Lock Banner */}
        {!isUnlimited && isOverLimit && (
          <Card className="mb-6 border-red-300 bg-red-50" data-testid="over-limit-banner">
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Lock className="h-6 w-6 text-red-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-red-700">
                    Your stash is over the limit by {itemsOver} item{itemsOver !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-red-600 mt-1">
                    You have {billingStatus?.stash_item_count} items but your current capacity is {billingStatus?.total_capacity}. 
                    You cannot add new items until you delete {itemsOver} item{itemsOver !== 1 ? 's' : ''} or upgrade your plan.
                  </p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    size="sm"
                    className="flex-1 sm:flex-initial bg-red-500 hover:bg-red-600 text-white"
                    onClick={() => navigate('/stash')}
                    data-testid="go-delete-items-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete Items
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 sm:flex-initial border-red-300 text-red-700"
                    onClick={() => document.getElementById('pricing-calculator')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid="upgrade-to-fix-btn"
                  >
                    <Zap className="h-4 w-4 mr-1" /> Upgrade
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unlimited Plan Card */}
        {isUnlimited && (
          <Card className="mb-6 border-2 border-amber-400 bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-50" data-testid="unlimited-plan-card">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-amber-500" />
              </div>
              <Badge className="bg-amber-400 text-white text-sm px-4 py-1 mb-3" data-testid="unlimited-badge">
                Unlimited
              </Badge>
              <h2 className="text-2xl font-medium text-charcoal mb-2">Unlimited Stash</h2>
              <p className="text-gray-soft max-w-md mx-auto mb-6">
                You have unlimited stash capacity. Add as many items as you like — no limits, no worries.
              </p>
              <div className="flex items-center justify-center gap-2 text-amber-600">
                <span className="text-4xl font-light" data-testid="unlimited-item-count">
                  {billingStatus?.stash_item_count || 0}
                </span>
                <span className="text-2xl text-amber-400">/</span>
                <span className="text-3xl" title="Unlimited capacity">&#8734;</span>
              </div>
              <p className="text-xs text-gray-soft mt-2">items in your stash</p>
            </CardContent>
          </Card>
        )}

        {/* Current Status (non-unlimited users) */}
        {!isUnlimited && (
        <Card className="mb-6" data-testid="billing-current-status">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-sage" />
                  Current Plan
                </CardTitle>
                <CardDescription>Your subscription and usage</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {accountState === 'over_limit' && (
                  <Badge className="bg-red-100 text-red-700" data-testid="over-limit-badge">
                    <AlertTriangle className="h-3 w-3 mr-1" /> Over Limit
                  </Badge>
                )}
                <Badge className={tierColors[currentTier]} data-testid="current-tier-badge">
                  {currentTier === 'cancelled' ? 'Cancelled' : currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current monthly cost */}
            {currentTier !== 'free' && currentTier !== 'cancelled' && (
              <div className="flex items-center justify-between p-4 bg-sage/5 border border-sage/20 rounded-lg" data-testid="current-cost-display">
                <div>
                  <p className="text-sm text-gray-soft">You're paying</p>
                  <p className="text-2xl font-semibold text-charcoal" data-testid="current-monthly-cost">
                    ${calculateMonthlyCost(billingStatus?.paid_item_slots || 0, tierRates)?.toFixed(2) || '0.00'}
                    <span className="text-sm font-normal text-gray-soft">/month</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-soft">
                    {billingStatus?.paid_item_slots || 0} paid items at ${getTierForPaidItems(billingStatus?.paid_item_slots || 0, tierRates)?.price_per_item?.toFixed(4) || '0.00'}/each
                  </p>
                </div>
              </div>
            )}
            {currentTier === 'free' && (
              <div className="flex items-center justify-between p-4 bg-sage/5 border border-sage/20 rounded-lg" data-testid="current-cost-display">
                <div>
                  <p className="text-sm text-gray-soft">You're paying</p>
                  <p className="text-2xl font-semibold text-charcoal">
                    $0.00
                    <span className="text-sm font-normal text-gray-soft">/month</span>
                  </p>
                </div>
                <div className="text-right">
                  <Badge className="bg-sage/10 text-sage">Free Plan</Badge>
                </div>
              </div>
            )}
            {currentTier === 'cancelled' && (
              <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="current-cost-display">
                <div>
                  <p className="text-sm text-gray-soft">You're paying</p>
                  <p className="text-2xl font-semibold text-charcoal">
                    $0.00
                    <span className="text-sm font-normal text-gray-soft">/month</span>
                  </p>
                </div>
                <div className="text-right">
                  <Badge className="bg-red-100 text-red-700">Cancelled</Badge>
                </div>
              </div>
            )}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-soft">Stash Usage</span>
                <span className={`font-medium ${isOverLimit ? 'text-red-600' : ''}`} data-testid="stash-usage-text">
                  {billingStatus?.stash_item_count || 0} / {billingStatus?.total_capacity || 20} items
                  {isOverLimit && ` (+${itemsOver} over)`}
                </span>
              </div>
              <div className="relative">
                <Progress
                  value={Math.min(usagePercent, 100)}
                  className={`h-2 ${isOverLimit ? '[&>div]:bg-red-500' : usagePercent >= 90 ? '[&>div]:bg-amber-500' : ''}`}
                />
              </div>
            </div>

            {/* Usage-based nudges */}
            {!isOverLimit && usagePercent >= 100 && (
              <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="at-limit-nudge">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-red-500 flex-shrink-0" />
                  <span className="text-red-700 text-sm font-medium">Your stash is full. Add more capacity to keep growing.</span>
                </div>
                <Button size="sm" className="ml-3 flex-shrink-0 bg-red-500 hover:bg-red-600 text-white text-xs"
                  onClick={() => document.getElementById('pricing-calculator')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="at-limit-upgrade-btn">
                  Add More Items
                </Button>
              </div>
            )}
            {usagePercent >= 90 && usagePercent < 100 && !isOverLimit && (
              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="near-limit-nudge">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  <span className="text-amber-700 text-sm">Almost full — {billingStatus?.items_until_full || 0} slots remaining</span>
                </div>
                <button className="ml-3 flex-shrink-0 text-amber-700 text-xs font-medium underline hover:text-amber-900"
                  onClick={() => document.getElementById('pricing-calculator')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="near-limit-upgrade-link">
                  Need more room?
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2">
              <div className="text-center p-3 bg-cream rounded-lg">
                <div className="text-2xl font-light text-charcoal" data-testid="paid-slots-count">
                  {billingStatus?.paid_item_slots || 0}
                </div>
                <div className="text-xs text-gray-soft">Paid Slots</div>
              </div>
              <div className="text-center p-3 bg-cream rounded-lg">
                <div className="text-2xl font-light text-charcoal" data-testid="free-items-count">
                  {billingStatus?.free_items_total || 20}
                </div>
                <div className="text-xs text-gray-soft">Free (Grant)</div>
              </div>
              <div className="text-center p-3 bg-cream rounded-lg">
                <div className="text-2xl font-light text-charcoal" data-testid="monthly-cost">
                  ${billingStatus?.monthly_cost?.toFixed(2) || '0.00'}
                </div>
                <div className="text-xs text-gray-soft">Monthly Cost</div>
              </div>
              <div className="text-center p-3 bg-cream rounded-lg">
                <div className="text-2xl font-light text-charcoal" data-testid="total-capacity-count">
                  {billingStatus?.total_capacity || 20}
                </div>
                <div className="text-xs text-gray-soft">Total Capacity</div>
              </div>
            </div>

            {/* Free items breakdown */}
            {billingStatus?.free_items_breakdown && (
              <div className="bg-cream/60 rounded-lg p-3 space-y-1" data-testid="free-items-breakdown">
                <p className="text-xs text-gray-soft font-medium uppercase tracking-wide mb-2">Free Items Breakdown</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-soft">Base grant</span>
                  <span className="text-charcoal">{billingStatus.free_items_breakdown.base_grant}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-soft">Referral bonus ({billingStatus.free_items_breakdown.referral_count} referrals)</span>
                  <span className="text-charcoal">{billingStatus.free_items_breakdown.referral_free}</span>
                </div>
                {billingStatus.free_items_breakdown.promo_free > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-soft">Promo bonus (temporary)</span>
                    <span className="text-sage">{billingStatus.free_items_breakdown.promo_free}</span>
                  </div>
                )}
                <div className="border-t border-beige pt-1 flex justify-between text-sm font-medium">
                  <span className="text-charcoal">Total free items</span>
                  <span className="text-charcoal">{billingStatus.free_items_breakdown.total}</span>
                </div>
              </div>
            )}

            {billingStatus?.stashed_away_count > 0 && (
              <button onClick={() => navigate('/stash/stashed-away')}
                className="w-full flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                data-testid="view-stashed-away-btn">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-700 text-sm">{billingStatus.stashed_away_count} items in Stashed Away</span>
                </div>
                <ChevronRight className="h-4 w-4 text-amber-600" />
              </button>
            )}
          
            {/* Billing contact */}
            <div className="border-t border-beige pt-4 mt-2" data-testid="billing-contact-footer">
              <p className="text-xs text-gray-soft leading-relaxed">
                Questions about your plan or charges?{' '}
                <button
                  onClick={() => setShowBillingFeedback(true)}
                  className="inline-flex items-center gap-1 text-sage hover:text-sage-dark underline underline-offset-2 font-medium"
                  data-testid="billing-contact-feedback-btn"
                >
                  <MessageSquarePlus size={12} />
                  Contact billing
                </button>
                {' '}or email{' '}
                <a
                  href="mailto:billing@kurastash.com"
                  className="inline-flex items-center gap-1 text-sage hover:text-sage-dark underline underline-offset-2 font-medium"
                  data-testid="billing-contact-email-link"
                >
                  <Mail size={12} />
                  billing@kurastash.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Pricing Slider / Calculator (hidden for unlimited users) */}
        {!isUnlimited && (
        <Card className="mb-6" id="pricing-calculator" data-testid="pricing-calculator">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-sage" />
              Size Your Stash
            </CardTitle>
            <CardDescription>
              Drag the slider to find your perfect stash size. Your rate automatically improves at tier thresholds.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Tier rate legend */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {tierRates.map((rate) => {
                const isActive = sliderTier.tier === rate.tier;
                const baseCost = calculateMonthlyCost(rate.min_items, tierRates);
                return (
                  <button key={rate.tier} onClick={() => setSliderPaidItems(rate.min_items)}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isActive ? 'bg-sage text-white ring-2 ring-sage/30' : 'bg-cream text-gray-soft hover:bg-sage-light/40'
                    }`} data-testid={`tier-indicator-${rate.tier}`}>
                    <span>{rate.name}</span>
                    <span className="opacity-75">${baseCost.toFixed(2)}/mo</span>
                  </button>
                );
              })}
            </div>

            {/* Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-soft">Paid item slots</span>
                  {recommendedPaidItems && sliderPaidItems === recommendedPaidItems && (
                    <span className="px-2 py-0.5 bg-rose/10 text-rose text-xs rounded-full font-medium" data-testid="recommended-badge">
                      Recommended for you
                    </span>
                  )}
                </div>
                <span className="text-lg font-medium text-charcoal" data-testid="slider-paid-items">{sliderPaidItems}</span>
              </div>
              <div className="relative">
                <div className="absolute top-1/2 -translate-y-1/2 w-full h-2 pointer-events-none">
                  {tierRates.slice(1).map((rate) => {
                    const pct = ((rate.min_items - 100) / (600 - 100)) * 100;
                    if (pct < 0 || pct > 100) return null;
                    return (
                      <div key={rate.tier} className="absolute top-0 w-0.5 h-4 -translate-y-1 bg-sage/40"
                        style={{ left: `${pct}%` }} title={`${rate.name} starts at ${rate.min_items}`} />
                    );
                  })}
                </div>
                <input type="range" min="100" max="600" step="10" value={sliderPaidItems}
                  onChange={(e) => setSliderPaidItems(parseInt(e.target.value))}
                  className="w-full h-2 bg-beige rounded-lg appearance-none cursor-pointer accent-sage"
                  data-testid="pricing-slider" />
                <div className="flex justify-between text-xs text-gray-soft mt-1">
                  <span>100</span><span>200</span><span>300</span><span>400</span><span>500</span><span>600</span>
                </div>
              </div>
              {recommendedPaidItems && (
                <p className="text-xs text-rose mt-2" data-testid="recommendation-note">
                  Based on your {currentItemCount} items, we suggest {recommendedPaidItems} paid slots for room to grow.
                  {recommendedPaidItems !== sliderPaidItems && (
                    <button
                      onClick={() => setSliderPaidItems(recommendedPaidItems)}
                      className="ml-1 underline font-medium hover:text-rose-dark"
                    >
                      Set to recommended
                    </button>
                  )}
                </p>
              )}
            </div>

            {/* Cost summary */}
            <div className="bg-white border border-beige rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-soft uppercase tracking-wide">Monthly Cost</div>
                  <div className="text-3xl font-light text-charcoal" data-testid="slider-monthly-cost">
                    ${sliderCost.toFixed(2)}<span className="text-sm text-gray-soft ml-1">/mo</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-soft uppercase tracking-wide">Total Capacity</div>
                  <div className="text-2xl font-light text-charcoal" data-testid="slider-total-capacity">
                    {sliderCapacity}<span className="text-xs text-gray-soft ml-1">items</span>
                  </div>
                </div>
              </div>

              <div className="bg-cream/60 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-soft">{sliderPaidItems} paid slots</span>
                  <span className="text-charcoal">
                    <Badge className={tierColors[sliderTier.tier] || 'bg-gray-100'} data-testid="slider-tier-badge">
                      {sliderTier.name}
                    </Badge>
                    <span className="ml-1.5 text-xs text-gray-soft">${sliderTier.price_per_item?.toFixed(4)}/item</span>
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-soft">+ {freeBaseGrant} free base items</span>
                  <span className="text-sage text-xs">always included</span>
                </div>
                {referralFree > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-soft">+ {referralFree} referral bonus</span>
                    <span className="text-sage text-xs">earned</span>
                  </div>
                )}
                {promoFree > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-soft">+ {promoFree} promo bonus</span>
                    <span className="text-sage text-xs">active</span>
                  </div>
                )}
                <div className="border-t border-beige pt-1 flex justify-between text-sm font-medium">
                  <span className="text-charcoal">= {sliderCapacity} total capacity</span>
                  <span className="text-charcoal">${sliderCost.toFixed(2)}/mo</span>
                </div>
              </div>

              {/* Nudge */}
              {nudge && (
                <div className="rounded-lg overflow-hidden border border-amber-200" data-testid="upgrade-nudge">
                  <div className="bg-amber-50 px-3 py-2 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span className="text-xs font-medium text-amber-800 uppercase tracking-wide">Save by upgrading</span>
                  </div>
                  <div className="px-3 py-3 bg-white space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="p-2 bg-red-50 rounded-lg">
                        <div className="text-xs text-gray-soft">Current ({sliderPaidItems} items)</div>
                        <div className="text-lg font-medium text-red-600">${nudge.currentCost.toFixed(2)}/mo</div>
                      </div>
                      <div className="p-2 bg-green-50 rounded-lg">
                        <div className="text-xs text-gray-soft">{nudge.targetPaidItems} items</div>
                        <div className="text-lg font-medium text-green-600">${nudge.targetCost.toFixed(2)}/mo</div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-soft text-center">
                      Get {nudge.extraItems} more items and save ${nudge.savings.toFixed(2)}/mo by crossing into the next tier
                    </p>
                    <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs"
                      onClick={() => setSliderPaidItems(nudge.targetPaidItems)} data-testid="nudge-apply-btn">
                      Jump to {nudge.targetPaidItems} items — save ${nudge.savings.toFixed(2)}/mo
                    </Button>
                  </div>
                </div>
              )}

              {/* Action button */}
              {currentTier !== 'cancelled' && (
                <div>
                  {isSame ? (
                    <Button disabled className="w-full" variant="outline" data-testid="current-plan-btn">
                      <Check className="h-4 w-4 mr-1" /> Current Plan
                    </Button>
                  ) : isUpgrade ? (
                    <Button className="w-full bg-sage hover:bg-sage-dark" onClick={() => showUpgradeConfirmation(sliderPaidItems)}
                      disabled={processingCheckout} data-testid="upgrade-btn">
                      {processingCheckout ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      {currentPaid === 0 ? `Subscribe — ${sliderPaidItems} paid items` : `Upgrade to ${sliderPaidItems} paid items`} @ ${sliderCost.toFixed(2)}/mo
                    </Button>
                  ) : isDowngrade ? (
                    cannotDowngrade ? (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-soft text-center">
                          You have <span className="font-semibold">{currentItemCount} items</span>. 
                          Delete <span className="font-semibold">{itemsOverLimit} items</span> to downgrade to {sliderPaidItems} paid slots.
                        </p>
                        <Button className="w-full bg-sage hover:bg-sage-dark" onClick={() => navigate('/stash')} data-testid="goto-stash-btn">
                          <Package className="h-4 w-4 mr-1" />
                          Take me to my Stash Now
                        </Button>
                      </div>
                    ) : (
                      <Button variant="outline" className="w-full" data-testid="downgrade-btn"
                        onClick={() => showUpgradeConfirmation(sliderPaidItems)}>
                        <ArrowDownCircle className="h-4 w-4 mr-1" />
                        Downgrade to {sliderPaidItems} paid items @ ${sliderCost.toFixed(2)}/mo
                      </Button>
                    )
                  ) : sliderPaidItems === 0 ? null : (
                    <Button className="w-full bg-sage hover:bg-sage-dark" onClick={() => showUpgradeConfirmation(sliderPaidItems)}
                      disabled={processingCheckout} data-testid="subscribe-btn">
                      {processingCheckout ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      Subscribe — {sliderPaidItems} paid items @ ${sliderCost.toFixed(2)}/mo
                    </Button>
                  )}
                </div>
              )}

              {/* For cancelled users, allow resubscription */}
              {currentTier === 'cancelled' && (
                <Button className="w-full bg-sage hover:bg-sage-dark" onClick={() => showUpgradeConfirmation(sliderPaidItems)}
                  disabled={processingCheckout} data-testid="reactivate-btn">
                  {processingCheckout ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Reactivate — {sliderPaidItems} paid items @ ${sliderCost.toFixed(2)}/mo
                </Button>
              )}
            </div>

            {/* Quick tier presets */}
            <div>
              <p className="text-xs text-gray-soft mb-2">Quick presets:</p>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries({ starter: 100, crafter: 200, artisan: 500 }).map(([tier, items]) => {
                  const cost = calculateMonthlyCost(items, tierRates);
                  const active = sliderPaidItems === items;
                  return (
                    <button key={tier} onClick={() => setSliderPaidItems(items)}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        active ? 'border-sage bg-sage-light/20' : 'border-beige hover:border-sage/50'
                      }`} data-testid={`preset-${tier}-btn`}>
                      <div className="text-sm font-medium text-charcoal capitalize">{tier}</div>
                      <div className="text-lg font-light text-charcoal">${cost.toFixed(2)}</div>
                      <div className="text-xs text-gray-soft">{items} paid items</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Referral Section */}
        <div className="mb-6">
          <ReferralShareWidget variant="full" isUnlimited={isUnlimited} />
        </div>

        {/* Promo Code Section (hidden for unlimited users) */}
        {!isUnlimited && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-sage" />
              Promo Code
            </CardTitle>
            <CardDescription>Have a promo code? Apply it for bonus free items</CardDescription>
          </CardHeader>
          <CardContent>
            <PromoCodeForm onSuccess={() => { fetchBillingData(); refreshUser(); }} />
          </CardContent>
        </Card>
        )}

        {/* Cancel Subscription (hidden for unlimited users) */}
        {!isUnlimited && currentTier !== 'free' && currentTier !== 'cancelled' && (
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Cancel Subscription
              </CardTitle>
              <CardDescription>
                Cancel your subscription. Your paid slots will be removed. If you have more items than your free limit, you'll need to delete items to regain full access.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => setShowCancelConfirm(true)} disabled={cancelLoading} data-testid="cancel-subscription-btn">
                {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Cancel Subscription
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Reactivate (hidden for unlimited users) */}
        {!isUnlimited && currentTier === 'cancelled' && (
          <Card className="border-sage">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sage">
                <RefreshCw className="h-5 w-5" />
                Reactivate Subscription
              </CardTitle>
              <CardDescription>
                Your data is still here. Use the slider above to choose a plan and reactivate.
                {isOverLimit && ` Note: You currently have ${itemsOver} items over your free limit. Reactivating with enough capacity will restore full access.`}
              </CardDescription>
            </CardHeader>
          </Card>
        )}
      </div>
      <AppFooter variant="minimal" />
      
      {/* Billing Feedback Modal */}
      <FeedbackWidget
        isOpen={showBillingFeedback}
        onClose={() => setShowBillingFeedback(false)}
        initialType="billing"
      />

      {/* Subscription Change Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, paidItems: 0 })}>
        <AlertDialogContent className="max-w-md bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(() => {
                const { isNewSubscription, isUpgrade, isDowngrade } = getConfirmationDetails();
                if (isNewSubscription) return 'Start Subscription';
                if (isUpgrade) return 'Upgrade Subscription';
                if (isDowngrade) return 'Downgrade Subscription';
                return 'Change Subscription';
              })()}
            </AlertDialogTitle>
            {(() => {
              const { currentPaid, newPaid, isNewSubscription, isUpgrade, isDowngrade } = getConfirmationDetails();
              const capacityChange = newPaid - currentPaid;
              
              return (
                <p className="text-sm text-gray-soft">
                  {isNewSubscription && (
                    <>You're adding <span className="font-semibold">{newPaid} paid slots</span> to your stash.</>
                  )}
                  {isUpgrade && (
                    <>You're gaining <span className="font-semibold">+{capacityChange} paid slots</span>.</>
                  )}
                  {isDowngrade && (
                    <>You're reducing your paid slots by <span className="font-semibold">{Math.abs(capacityChange)}</span>.</>
                  )}
                </p>
              );
            })()}
            <AlertDialogDescription asChild>
              <div className="space-y-4 mt-2">
                {(() => {
                  const { currentPaid, currentTier, currentCost, newPaid, newTier, newCost, isNewSubscription, isDowngrade } = getConfirmationDetails();
                  return (
                    <>
                      {!isNewSubscription && (
                        <div className={`rounded-lg p-3 ${isDowngrade ? 'bg-gray-50' : 'bg-gray-50'}`}>
                          <p className="text-xs text-gray-soft mb-1">Current Plan</p>
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-lg text-charcoal">{currentPaid} paid items</span>
                            <Badge variant="outline" className="capitalize">{currentTier}</Badge>
                          </div>
                          <p className="text-sm text-gray-soft">${currentCost.toFixed(2)}/month</p>
                        </div>
                      )}
                      
                      {!isNewSubscription && (
                        <div className="flex justify-center -my-3 relative z-10">
                          <div className="bg-white rounded-full p-1">
                            <ArrowDownCircle className="h-8 w-8 text-sage" />
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-sage/10 border border-sage/30 rounded-lg p-3">
                        <p className="text-xs text-sage-dark mb-1">{isNewSubscription ? 'Your New Plan' : 'New Plan'}</p>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-lg text-charcoal">{newPaid} paid items</span>
                          <Badge className="bg-sage text-white capitalize">{newTier}</Badge>
                        </div>
                        <p className="text-sm text-gray-soft">${newCost.toFixed(2)}/month</p>
                      </div>
                      
                      <p className="text-xs text-gray-soft text-center">
                        {isNewSubscription 
                          ? 'You will be redirected to Stripe to complete payment.'
                          : 'Your subscription will be updated immediately. Any price difference will be prorated.'}
                      </p>
                    </>
                  );
                })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedCheckout} className="bg-sage hover:bg-sage-dark">
              {(() => {
                const { isNewSubscription, isUpgrade, isDowngrade } = getConfirmationDetails();
                if (isNewSubscription) return 'Continue to Payment';
                if (isUpgrade) return 'Confirm Upgrade';
                if (isDowngrade) return 'Confirm Downgrade';
                return 'Confirm Change';
              })()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Subscription Confirmation */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Your paid slots will be removed. If you have more items than your free limit, you'll need to delete items to regain full access. Your data will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelLoading}>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={cancelLoading}
              className="bg-red-500 hover:bg-red-600"
            >
              {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Cancel Subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== Promo Code Form ====================
const PromoCodeForm = ({ onSuccess }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/promo/apply`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ code: code.trim() })
      });
      const data = await res.clone().json();
      if (res.ok) { toast.success(data.message || 'Promo code applied!'); setCode(''); onSuccess?.(); }
      else { toast.error(data.detail || 'Invalid promo code'); }
    } catch { toast.error('Failed to apply promo code'); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input type="text" value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="Enter promo code"
        className="flex-1 px-3 py-2 border border-beige rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/50"
        data-testid="promo-code-input" />
      <Button type="submit" disabled={loading || !code.trim()} className="bg-sage hover:bg-sage-dark" data-testid="apply-promo-btn">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
      </Button>
    </form>
  );
};

export default BillingPage;
